import { RootSelector, getStore } from '../../reducers';
import { CategoryShape, ALL_CATEGORIES_ID, ALL_CATEGORIES_NAME } from './model';
import { createSelector } from '@reduxjs/toolkit';
import { Category } from '../../api';
import { DataNode } from 'rc-tree/lib/interface';

export const getCategory: RootSelector<CategoryShape> = createSelector(
  getStore,
  (state) => state.category,
);

export const getCategories: RootSelector<Category[]> = createSelector(
  getCategory,
  (category) => category.categories,
);

export const getCategoriesById: RootSelector<Partial<Record<Category['id'], Category>>> =
  createSelector(getCategory, (category) => {
    const result: Partial<Record<Category['id'], Category>> = {};
    category.categories.forEach((category) => {
      result[category.id] = category;
    });
    return result;
  });

export const getSelectedId: RootSelector<Category['id']> = createSelector(
  getCategory,
  (category) => category.selectedId,
);

export const getTreeData: RootSelector<DataNode[]> = createSelector(
  [getCategories],
  (categories) => {
    // В antd есть компонент TreeSelect, который в отличие от обычного Tree поддерживает опцию
    // treeDataSimpleMode. Т.е. TreeSelect умеет превращать плоские списки нод в древовидную
    // структуру. Это происходит в node_modules/rc-tree-select/lib/hooks/useTreeData.js в функции
    // parseSimpleTreeData. Возьмем за основу эту функцию, чтобы превратить наш плоский список в
    // то дерево, которое требуется для Tree.

    const keyNodes: Partial<Record<Category['id'], DataNode>> = {};
    const rootNodeList: DataNode[] = [];

    type Node = DataNode & { parentKey: Category['id'] | null };

    const nodeList: Node[] = categories.map((category) => {
      const node = {
        key: category.id,
        parentKey: category.path.length > 0 ? category.path[category.path.length - 1] : null,
        title: category.name,
      };
      keyNodes[node.key] = node;
      return node;
    });

    nodeList.forEach((node) => {
      if (node.parentKey === null) {
        rootNodeList.push(node);
        return;
      }
      const parent = keyNodes[node.parentKey];
      if (parent) {
        parent.children = parent.children || [];
        parent.children.push(node);
      }
    });

    // Категория All не приходит с бэка. Создаем эту псевдо-категорию на фронте с уникальным id.
    const all = {
      key: ALL_CATEGORIES_ID,
      title: ALL_CATEGORIES_NAME,
      children: rootNodeList,
    };

    return [all];
  },
);

export const getSelectedKeys: RootSelector<Category['id'][]> = createSelector(
  getSelectedId,
  (selectedId) => {
    return [selectedId];
  },
);

export const getExpandedKeys: RootSelector<Category['id'][]> = createSelector(
  getCategory,
  (category) => category.expandedKeys,
);

export const getLoading: RootSelector<boolean> = createSelector(
  getCategory,
  (category) => category.loading,
);

export const getCategoriesCollapsed = createSelector(
  [getSelectedId, getCategoriesById],
  (selectedId, categoriesById) => {
    const result =
      selectedId === ALL_CATEGORIES_ID
        ? ALL_CATEGORIES_NAME
        : categoriesById[selectedId]?.name ?? '';
    return result;
  },
);
